/**
 * US-Customary-aware parser for values a HUMAN types.
 *
 * ─── Why this is not `unitNormalize` ──────────────────────────────────
 *
 * `src/lib/audit/unitNormalize.ts` parses quantities out of GENERATED PROSE,
 * and it is deliberately permissive: an unrecognised unit falls through to the
 * canonical one, because a narrative that says "204.9" almost certainly means
 * the canonical unit and a missed extraction is worse than a loose one.
 *
 * User input is the opposite problem. The truth-resolution dialog writes the
 * operator's answer straight into a typed SED field, which then becomes a
 * VERIFIED tier-1 canonical fact. Before this module the answer was written as
 * a raw string with no unit handling at all, so typing "1.9 miles" into the
 * substation-distance field stored the string `"1.9 miles"` where a `number` of
 * kilometres was declared — a corrupt fact carrying more authority than the
 * provider value it replaced, and worse than the block it resolved.
 *
 * So: permissive for prose, STRICT for people. An unrecognised unit, or one
 * belonging to a different dimension, is an ERROR here. Silently coercing
 * "1.9 miles" to 1.9 km is the failure this exists to prevent.
 *
 * ─── Conventions ──────────────────────────────────────────────────────
 *
 * Conversion factors are named constants (never inline magic numbers) and are
 * exact where the definition is exact: the international mile, foot and yard
 * are defined exactly in terms of the metre, and the international acre is
 * defined exactly as 4046.8564224 m². Only `HA_TO_ACRES` is a rounded
 * reciprocal, reused from `unitNormalize` so the two modules cannot disagree.
 */
import { GW_TO_MW, HA_TO_ACRES, MI_TO_KM } from '../audit/unitNormalize';

/** Exact by definition: 1 international foot = 0.3048 m. */
export const FT_TO_KM = 0.0003048;
/** Exact by definition: 1 international yard = 0.9144 m. */
export const YD_TO_KM = 0.0009144;
/** 1 international nautical mile = 1852 m exactly. */
export const NMI_TO_KM = 1.852;
export const M_TO_KM = 0.001;
/** 1 square mile = 640 acres exactly (US survey township arithmetic). */
export const SQMI_TO_ACRES = 640;
/** 1 acre = 43,560 ft² exactly. */
export const SQFT_TO_ACRES = 1 / 43560;
export const KW_TO_MW = 0.001;
export const KV_TO_V = 1000;

interface UnitAlias {
  /** Matched against the unit token, case-insensitively, anchored. */
  match: RegExp;
  /** Multiply the parsed number by this to reach the canonical unit. */
  factor: number;
  /** Canonical spelling, echoed back so the UI can show what was understood. */
  label: string;
}

/**
 * Accepted input units PER CANONICAL TARGET. A unit absent from its target's
 * list is rejected — that is what stops an area being written into a distance
 * field. Note `acres` and `mi` are themselves US customary, so these tables run
 * in both directions: an operator may equally type hectares into an acres field.
 */
const CONVERSIONS: Readonly<Record<string, readonly UnitAlias[]>> = {
  km: [
    { match: /^(?:km|kilometres?|kilometers?)$/i, factor: 1, label: 'km' },
    { match: /^(?:mi|miles?)$/i, factor: MI_TO_KM, label: 'mi' },
    { match: /^(?:ft|foot|feet)$/i, factor: FT_TO_KM, label: 'ft' },
    { match: /^(?:yd|yards?)$/i, factor: YD_TO_KM, label: 'yd' },
    { match: /^(?:nmi|nautical\s*miles?)$/i, factor: NMI_TO_KM, label: 'nmi' },
    { match: /^(?:m|metres?|meters?)$/i, factor: M_TO_KM, label: 'm' },
  ],
  mi: [
    { match: /^(?:mi|miles?)$/i, factor: 1, label: 'mi' },
    { match: /^(?:km|kilometres?|kilometers?)$/i, factor: 1 / MI_TO_KM, label: 'km' },
    { match: /^(?:ft|foot|feet)$/i, factor: FT_TO_KM / MI_TO_KM, label: 'ft' },
  ],
  acres: [
    { match: /^(?:ac|acres?)$/i, factor: 1, label: 'acres' },
    { match: /^(?:ha|hectares?)$/i, factor: HA_TO_ACRES, label: 'ha' },
    { match: /^(?:sq\s*mi|square\s*miles?|mi2|mi²)$/i, factor: SQMI_TO_ACRES, label: 'sq mi' },
    { match: /^(?:sq\s*ft|square\s*feet|ft2|ft²)$/i, factor: SQFT_TO_ACRES, label: 'sq ft' },
  ],
  MW: [
    { match: /^(?:mw|megawatts?)$/i, factor: 1, label: 'MW' },
    { match: /^(?:gw|gigawatts?)$/i, factor: GW_TO_MW, label: 'GW' },
    { match: /^(?:kw|kilowatts?)$/i, factor: KW_TO_MW, label: 'kW' },
  ],
  MVA: [
    { match: /^(?:mva)$/i, factor: 1, label: 'MVA' },
    { match: /^(?:kva)$/i, factor: KW_TO_MW, label: 'kVA' },
  ],
  kV: [
    { match: /^(?:kv|kilovolts?)$/i, factor: 1, label: 'kV' },
    { match: /^(?:v|volts?)$/i, factor: 1 / KV_TO_V, label: 'V' },
  ],
};

/** Units that carry no dimension — a bare number is the only sensible input. */
const DIMENSIONLESS = new Set(['count', 'USD', 'USD/MWh']);

/**
 * Did the operator simply write the canonical unit back? Case- and
 * space-insensitive equality only — this authorises no conversion, it just
 * stops the parser rejecting its own unit.
 */
function isEchoedUnit(token: string, targetUnit: string): boolean {
  const norm = (u: string): string => u.replace(/\s+/g, '').toLowerCase();
  return norm(token) === norm(targetUnit);
}

export interface ParsedQuantity {
  /** The value expressed in the TARGET canonical unit. */
  value: number;
  /** Canonical spelling of what the operator actually typed. */
  inputUnit: string;
  /** True when a unit conversion was applied (drives the UI echo). */
  converted: boolean;
  /**
   * True when the input carried NO unit and the canonical one was assumed.
   * The caller should say so rather than silently accepting — an unlabelled
   * number is the most likely way a wrong unit enters undetected.
   */
  assumedCanonical: boolean;
}

export interface QuantityError { error: string }

export function isQuantityError(r: ParsedQuantity | QuantityError): r is QuantityError {
  return (r as QuantityError).error !== undefined;
}

/** Accepted input units for a target, for placeholder/help text. */
export function acceptedUnits(targetUnit: string): string[] {
  return (CONVERSIONS[targetUnit] ?? []).map((a) => a.label);
}

/**
 * Parse a human-typed quantity into the target canonical unit.
 *
 * Accepts `"3.01"`, `"1.9 miles"`, `"1,170.1 acres"`, `"6,200 ft"`, `"0.55 GW"`,
 * `"$1,200,000"`. Rejects an empty string, a non-numeric string, and — the
 * point of the exercise — a unit from the wrong dimension.
 */
export function parseUserQuantity(
  input: string,
  targetUnit: string | undefined,
): ParsedQuantity | QuantityError {
  // Total, like the severity policy: this sits on a user-input path and a
  // throw here would take the whole resolution dialog down.
  if (typeof input !== 'string') return { error: 'Enter a value.' };
  const raw = input.trim();
  if (!raw) return { error: 'Enter a value.' };

  // Currency symbols and thousands separators are noise, never dimension.
  const cleaned = raw.replace(/[$£€]/g, '').replace(/,/g, '').trim();

  const m = /^(-?\d+(?:\.\d+)?)\s*(.*)$/.exec(cleaned);
  if (!m) return { error: `"${raw}" is not a number.` };

  const value = Number.parseFloat(m[1]!);
  if (!Number.isFinite(value)) return { error: `"${raw}" is not a number.` };
  const unitToken = (m[2] ?? '').trim();

  // No target unit declared (entity/categorical facts) — a bare number only.
  if (!targetUnit || DIMENSIONLESS.has(targetUnit)) {
    if (unitToken) return { error: `Remove "${unitToken}" — enter a plain number.` };
    return { value, inputUnit: targetUnit ?? '', converted: false, assumedCanonical: true };
  }

  const aliases = CONVERSIONS[targetUnit];
  if (!aliases) {
    // An unmapped canonical unit: accept a bare number, refuse to guess a
    // conversion we have no table for.
    //
    // ECHOED CANONICAL UNIT. Naming the target unit is AGREEMENT, not a
    // conversion request, so it is accepted rather than refused. Without this,
    // a `%` fact rejected "92%" with "Enter the value in %." — indicting the
    // operator for typing exactly the unit it asked for. Reachable for every
    // unit outside the conversion table (`%`, `USD/kW`, `MWh`, `years`).
    // Deliberately an EQUALITY test, not a fuzzy match: nothing is converted,
    // so a token that merely resembles the target is still refused.
    if (unitToken && !isEchoedUnit(unitToken, targetUnit)) {
      return { error: `Enter the value in ${targetUnit}.` };
    }
    return { value, inputUnit: targetUnit, converted: false, assumedCanonical: !unitToken };
  }

  if (!unitToken) {
    return { value, inputUnit: targetUnit, converted: false, assumedCanonical: true };
  }

  const hit = aliases.find((a) => a.match.test(unitToken));
  if (!hit) {
    return {
      error: `"${unitToken}" is not a valid unit for ${targetUnit}. Use one of: ${aliases.map((a) => a.label).join(', ')}.`,
    };
  }

  return {
    value: value * hit.factor,
    inputUnit: hit.label,
    converted: hit.factor !== 1,
    assumedCanonical: false,
  };
}
